import React, { useState, useContext, useEffect } from "react"
import PropTypes from "prop-types"
import { gql, useLazyQuery, useQuery } from "@apollo/client"
import { Field, useFormikContext } from "formik"
import { MultiSelect } from "primereact/multiselect"
import { FormContext } from "../../../context/formContext"
import { UserContext } from "../../../context/userContext"
import { LoaderSmall } from "../../Loaders"
import { LogDebug, LogError } from "../../../helpers/logger"

import styles from "../../../styles/Form.module.css"

const ProgramIdentityId = ({ field, block, index }) => {
  const [activePrograms, setActivePrograms] = useState([])
  const [inactivePrograms, setInactivePrograms] = useState([])
  const [loadingAssignedPrograms, setLoadingAssignedPrograms] = useState(false)
  const [programOptionsLoaded, setProgramOptionsLoaded] = useState(false)

  const form = useContext(FormContext)
  const userCtx = useContext(UserContext)
  const formik = useFormikContext()

  let isDisabled = form ? form.blocksSaved[block] : false
  isDisabled = field.read_only ? true : isDisabled

  const safeArray = (value) => (Array.isArray(value) ? value : [])
  const findByKey = (optionsList, key) =>
    safeArray(optionsList).find((option) => option.key === key)

  const checkIfRequired = () => {
    try {
      return JSON.parse(field.field_validation.required[0])
    } catch (error) {
      return false
    }
  }

  const isRequired = checkIfRequired()

  // -----------------------------
  // 🔧 Remap selected values by key
  // -----------------------------
  const remapByKey = (
    selectedList,
    optionsList,
    fallbackOptionsList = [],
    shouldWarn = false,
  ) => {
    const safeSelected = safeArray(selectedList)

    return safeSelected
      .map((item) => {
        const match = findByKey(optionsList, item.key)

        // The same selected value is checked against active and inactive lists.
        // Only warn after the option lists have loaded and the key is missing from both.
        if (shouldWarn && !match && !findByKey(fallbackOptionsList, item.key)) {
          LogDebug(
            "[ProgramIdentityId] Missing option for key:",
            item.key,
            "Selected item was:",
            item,
          )
        }
        return match || null
      })
      .filter(Boolean)
  }

  // -----------------------------
  // GraphQL Queries
  // -----------------------------
  const GET_PROGRAMS = gql`
    query getPrograms($staff_id: ID!, $organization_id: ID!) {
      programs: programsByOrganization(
        staff_id: $staff_id
        organization_id: $organization_id
      ) {
        key: program_identity_id
        label: program_id_name
        status: program_id_status
      }
    }
  `

  const GET_ASSIGNED_PROGRAMS = gql`
    query getAssignedPrograms(
      $participant_membership_id: ID!
      $staff_id: ID!
      $organization_id: ID!
    ) {
      programsByMembershipId(
        participant_membership_id: $participant_membership_id
        staff_id: $staff_id
        organization_id: $organization_id
      ) {
        key: program_identity_id
        label: program_id_name
        status: program_id_status
      }
    }
  `

  const [fetchAssignedPrograms] = useLazyQuery(GET_ASSIGNED_PROGRAMS, {
    fetchPolicy: "network-only",
  })

  /** Initialize field value safely */
  useEffect(() => {
    form && form.registerLoadingField(field)

    const init = async () => {
      const raw = formik.initialValues?.[field.field_name]

      if (Array.isArray(raw)) {
        formik.setFieldValue(field.field_name, raw)
      } else if (raw && typeof raw === "object") {
        formik.setFieldValue(field.field_name, [raw])
      } else {
        if (!form?.editMode) {
          await applyAssignedPrograms()
        } else {
          formik.setFieldValue(field.field_name, [])
        }
      }

      // Notify FormContext that field is loaded
      form && form.completeLoadingField(field)
    }
    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps, no-sparse-arrays
  }, [])

  async function applyAssignedPrograms() {
    let assignedPrograms = []
    setLoadingAssignedPrograms(true)

    try {
      // Check if user context has necessary info to fetch assigned programs
      if (
        userCtx?.participant_membership_id &&
        userCtx?.session?.user?.staff_id &&
        userCtx?.session?.user?.organization_id
      ) {
        // Fetch assigned programs for the participant
        const assignedProgramsResult = await fetchAssignedPrograms({
          variables: {
            participant_membership_id: userCtx?.participant_membership_id,
            staff_id: userCtx.session.user.staff_id,
            organization_id: userCtx.session.user.organization_id,
          },
        })

        // Set assigned programs if data is returned
        if (assignedProgramsResult?.data?.programsByMembershipId) {
          assignedPrograms =
            assignedProgramsResult.data.programsByMembershipId.map((prog) => ({
              key: prog.key,
              label: prog.label,
            }))
        }
      }
    } catch (error) {
      LogError("Error fetching assigned programs:", error)
    } finally {
      setLoadingAssignedPrograms(false)
    }

    // Apply assigned programs to Formik field value
    formik.setFieldValue(field.field_name, assignedPrograms)
  }

  const { loading: loadingPrograms } = useQuery(GET_PROGRAMS, {
    fetchPolicy: "network-only",
    variables: {
      staff_id: userCtx.session.user.staff_id,
      organization_id: userCtx.session.user.organization_id,
    },
    skip: !userCtx,
    onCompleted: ({ programs }) => {
      const activeProgramsList =
        programs
          .filter((program) => program.status === "ACTIVE")
          .map((program) => ({
            key: program.key,
            label: program.label,
          })) || []

      const inactiveProgramsList =
        programs
          .filter((program) => program.status === "INACTIVE")
          .map((program) => ({
            key: program.key,
            label: program.label,
          })) || []

      setActivePrograms(activeProgramsList)
      setInactivePrograms(inactiveProgramsList)
      setProgramOptionsLoaded(true)
    },
  })

  // -----------------------------
  // Handlers
  // -----------------------------
  const handleSelectProgramIds = (value) => {
    if (value === null) {
      formik.setFieldValue(field.field_name, [])
      return
    }

    const fieldCurrentValue = safeArray(formik.values[field.field_name])

    const currentInactive = fieldCurrentValue.filter((item) =>
      inactivePrograms.some((inactiveItem) => inactiveItem.key === item.key),
    )

    const newValue = [...currentInactive, ...safeArray(value)]
    formik.setFieldValue(field.field_name, newValue)
  }

  const handleDeselectInactiveProgramIds = (value) => {
    const safeVal = safeArray(value)

    if (safeVal.length === 0) {
      const selectedPrograms = safeArray(
        formik.values[field.field_name],
      ).filter((item) =>
        activePrograms.find((activeItem) => activeItem.key === item.key),
      )

      formik.setFieldValue(field.field_name, selectedPrograms)
      return
    }

    const fieldCurrentValue = safeArray(formik.values[field.field_name])

    const currentActive = fieldCurrentValue.filter((item) =>
      activePrograms.some((activeItem) => activeItem.key === item.key),
    )

    const updated = [...currentActive, ...safeVal]
    setInactivePrograms(safeVal)
    formik.setFieldValue(field.field_name, updated)
  }

  // -----------------------------
  // Render MultiSelect (with remap)
  // -----------------------------
  const handleMultiSelectRender = (formikField) => {
    const currentValues = safeArray(formikField?.value)

    // Each remap call receives the opposite list as fallback to avoid false warnings
    // when a selected program exists in only one of the two lists.
    const currentActiveSelectedPrograms = remapByKey(
      currentValues,
      activePrograms,
      inactivePrograms,
      programOptionsLoaded,
    )
    const currentInactiveSelectedPrograms = remapByKey(
      currentValues,
      inactivePrograms,
      activePrograms,
      programOptionsLoaded,
    )

    const renderInactivePrograms = () => {
      if (!form?.editMode || currentInactiveSelectedPrograms.length === 0)
        return null

      return (
        <div>
          <label className={styles.label}>Inactive Programs ID(s)</label>
          <div className={styles.description}>
            Inactive programs can only be deselected.
          </div>

          <MultiSelect
            placeholder="Unselect only"
            id={field.field_name}
            name={formikField.name}
            value={currentInactiveSelectedPrograms}
            onChange={(e) => handleDeselectInactiveProgramIds(e.value)}
            onBlur={(e) => formik.handleBlur(e)}
            disabled={isDisabled}
            options={inactivePrograms}
          />
        </div>
      )
    }

    return (
      <>
        <MultiSelect
          placeholder="Please select..."
          id={field.field_name}
          name={formikField.name}
          value={currentActiveSelectedPrograms}
          onChange={(e) => handleSelectProgramIds(e.value)}
          onBlur={(e) => formik.handleBlur(e)}
          disabled={isDisabled}
          options={activePrograms}
        />

        {renderInactivePrograms()}
      </>
    )
  }

  if (loadingPrograms || loadingAssignedPrograms) return <LoaderSmall />

  return (
    <div
      className={styles.field}
      style={{
        order: index,
      }}
    >
      <label className={styles.label}>
        {field.field_label}
        {isRequired && <span className={styles.asterisk}> *</span>}
      </label>

      {field.field_description && (
        <div className={styles.description}>{field.field_description}</div>
      )}

      <div className={styles.value}>
        <Field name={field.field_name}>
          {({ field: formikField, meta }) => (
            <>
              {handleMultiSelectRender(formikField)}
              {meta.touched && meta.error && (
                <div className={styles.errFieldMsg}>{meta.error}</div>
              )}
            </>
          )}
        </Field>
      </div>
    </div>
  )
}

ProgramIdentityId.propTypes = {
  field: PropTypes.object.isRequired,
  block: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
}

export default ProgramIdentityId
